import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";

const heroMannUrl = "/__l5e/assets-v1/64e3d466-a23a-42dd-a208-adeb041acf73/eve-hero-mann.jpg";

export const Route = createFileRoute("/was-ist-energy-prime-360")({
  head: () => ({
    meta: [
      { title: "Was ist Energy Prime 360? – Unabhängige Analyse- und Vergleichsplattform" },
      {
        name: "description",
        content:
          "Energy Prime 360 ist Ihr unabhängiges, digitales Analyse- und Vergleichsportal für Energie, Gebäude und Förderung – 100 % unabhängig und kostenfrei.",
      },
      { property: "og:title", content: "Was ist Energy Prime 360?" },
      {
        property: "og:description",
        content: "Unabhängig. Herstellerneutral. Alle Faktoren im Blick.",
      },
      { property: "og:url", content: "https://www.energie-prime.de/was-ist-energy-prime-360" },
    ],
    links: [{ rel: "canonical", href: "https://www.energie-prime.de/was-ist-energy-prime-360" }],
  }),
  component: Page,
});

function Check({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-navy/85">
      <span className="inline-flex w-5 h-5 rounded-full bg-[color-mix(in_oklab,var(--leaf)_15%,white)] items-center justify-center shrink-0">
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[var(--leaf-deep)]" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}

type Feature = {
  title: string;
  body: string;
  color: string;
  icon: React.ReactNode;
};

const features: Feature[] = [
  {
    title: "Unabhängiges Analyse- und Vergleichsportal",
    body: "Wir arbeiten hersteller- und anbieterneutral. So erhalten Sie eine objektive Analyse und Vergleich von Lösungen, Produkten und Angeboten.",
    color: "bg-navy",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M12 2L4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3zm-1.2 14.2l-3.5-3.5 1.4-1.4 2.1 2.1 5-5 1.4 1.4-6.4 6.4z" />
      </svg>
    ),
  },
  {
    title: "Herstellerneutral und transparent",
    body: "Alle Technologien und Anbieter werden gleich bewertet. Sie erhalten volle Transparenz über Chancen, Risiken, Kosten und Leistungen.",
    color: "bg-[var(--leaf-deep)]",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M4 21V9l8-6 8 6v12h-5v-6h-6v6H4zm5-9h2v2H9v-2zm4 0h2v2h-2v-2zM9 8h2v2H9V8zm4 0h2v2h-2V8z" />
      </svg>
    ),
  },
  {
    title: "Alle relevanten Faktoren im Blick",
    body: "Technik, Wirtschaftlichkeit, Förderung, Steuern, Risiken und Praxisbeispiele – wir betrachten das große Ganze für Ihre individuelle Situation.",
    color: "bg-orange",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M10 4a6 6 0 104.47 10.03l4.25 4.25 1.41-1.41-4.25-4.25A6 6 0 0010 4zm0 2a4 4 0 110 8 4 4 0 010-8z" />
      </svg>
    ),
  },
  {
    title: "Fundierte Entscheidungsgrundlage",
    body: "Vergleichbare Analysen, klare Empfehlungen und verständliche Ergebnisse – für Entscheidungen mit Sicherheit und Weitblick.",
    color: "bg-[#7e3af2]",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M19 3h-4.18A3 3 0 0012 1a3 3 0 00-2.82 2H5a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm-7 0a1 1 0 110 2 1 1 0 010-2zm-1.2 15.2l-3.5-3.5 1.4-1.4 2.1 2.1 5-5 1.4 1.4-6.4 6.4z" />
      </svg>
    ),
  },
];

type Vorteil = { title: string; sub: string; icon: React.ReactNode };

const vorteile: Vorteil[] = [
  {
    title: "Kosten sparen",
    sub: "Die beste Lösung für Ihr Budget finden",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M19 9.5a1.5 1.5 0 11-1.5-1.5h.5V7h-1A6 6 0 008 4a6 6 0 00-6 6c0 2 1 3.7 2.5 4.8L4 17h3l.5-1.2c.5.1 1 .2 1.5.2H13l1 2h3l-.5-2.3c1-.7 1.7-1.7 2.2-2.9H21V9.5h-2zM9 11a1 1 0 110-2 1 1 0 010 2z" />
      </svg>
    ),
  },
  {
    title: "Risiken minimieren",
    sub: "Fundierte Analysen statt Bauchgefühl",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a6 6 0 110 12 6 6 0 010-12zm0 3a3 3 0 100 6 3 3 0 000-6z" />
      </svg>
    ),
  },
  {
    title: "Nachhaltig entscheiden",
    sub: "Für Umwelt, Zukunft und Generationen",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M17 8C8 10 5 16 4 22h2c.7-3 2-5 4-7v6h2v-6c4-1 6-4 5-7zm-2-5C9 4 5 8 7 14c3-3 6-4 8-11z" />
      </svg>
    ),
  },
  {
    title: "Zeit gewinnen",
    sub: "Alle Informationen an einem Ort",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 11h-5v-2h3V6h2v7z" />
      </svg>
    ),
  },
  {
    title: "Sicherheit erhalten",
    sub: "Klare Empfehlungen mit Weitblick",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2L4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3z" />
      </svg>
    ),
  },
];

function Page() {
  return (
    <div className="min-h-screen bg-[#f4f6fa] text-navy">
      <SiteNav />
      <main>
        {/* HERO */}
        <section className="border-b border-[var(--rule)]">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-10 md:py-16 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h1 className="font-display font-bold text-navy text-3xl md:text-5xl leading-[1.05]">
                Was ist<br />
                Energy Prime <span className="text-orange">360</span>?
              </h1>
              <p className="mt-5 text-navy/80 text-base md:text-lg leading-relaxed max-w-[520px]">
                Energy Prime 360 ist Ihr unabhängiges, digitales Analyse- und Vergleichsportal für
                Energie, Gebäude und Förderung.
              </p>
              <p className="mt-3 text-navy/80 text-base md:text-lg leading-relaxed max-w-[520px]">
                Wir bündeln Daten, vergleichen Optionen und bereiten alle relevanten Faktoren auf –
                damit Sie sicher und fundiert entscheiden können.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                <Check>100 % unabhängig</Check>
                <Check>100 % kostenfrei &amp; unverbindlich</Check>
              </div>
            </div>
            <div className="relative">
              <img
                src={heroMannUrl}
                alt="Kunde nutzt Energy Prime 360 zur Analyse von Energieoptionen, Förderung und Wirtschaftlichkeit"
                width={1280}
                height={960}
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* FEATURE CARDS */}
        <section className="border-b border-[var(--rule)]">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-12 md:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {features.map((f) => (
                <article
                  key={f.title}
                  className="bg-white rounded-2xl border border-[var(--rule)] shadow-sm p-6 flex flex-col items-center text-center hover:shadow-md transition"
                >
                  <span className={`w-14 h-14 rounded-full ${f.color} inline-flex items-center justify-center mb-4 shadow-sm`}>
                    {f.icon}
                  </span>
                  <h3 className="font-display font-semibold text-navy text-base leading-snug mb-3 min-h-[3rem]">
                    {f.title}
                  </h3>
                  <p className="text-sm text-navy/70 leading-relaxed">{f.body}</p>
                  <span className="mt-4 text-[var(--leaf-deep)] text-xl" aria-hidden>→</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* VORTEILE STRIP */}
        <section className="border-b border-[var(--rule)]" style={{ backgroundColor: "#eef1f5" }}>
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-10 md:py-12">
            <h2 className="text-center font-display font-bold text-navy text-xl md:text-2xl mb-8">
              Ihre Vorteile auf einen Blick
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
              {vorteile.map((v) => (
                <div key={v.title} className="flex items-start gap-3 bg-white rounded-xl border border-[var(--rule)] p-4">
                  <span className="text-[var(--leaf-deep)] shrink-0 mt-0.5">{v.icon}</span>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-navy text-sm leading-tight">{v.title}</div>
                    <div className="text-xs text-navy/65 mt-1 leading-snug">{v.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div>
              <h2 className="font-display font-bold text-navy text-2xl md:text-3xl leading-tight">
                Bereit für Ihre optimale Entscheidung?
              </h2>
              <p className="mt-4 text-navy/80 text-base leading-relaxed max-w-[540px]">
                Starten Sie jetzt Ihre unabhängige Analyse und erhalten Sie klare Empfehlungen –
                kostenlos und unverbindlich.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                <Check>In nur 2 Minuten Projekt starten</Check>
                <Check>100 % kostenlos &amp; unverbindlich</Check>
              </div>
              <Link
                to="/projekt-beschreiben"
                className="mt-7 inline-flex items-center gap-2 bg-orange hover:bg-orange/90 text-white font-semibold px-6 py-3 rounded-md transition shadow-md"
              >
                Kostenlose Analyse starten
                <span aria-hidden>→</span>
              </Link>
            </div>
            <CtaIllustration />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function CtaIllustration() {
  const items = [
    { label: "Energie", d: "M13 2L3 14h7l-1 8 10-12h-7l1-8z" },
    { label: "Gebäude", d: "M4 21V9l8-6 8 6v12h-5v-6h-6v6H4z" },
    { label: "Förderung", d: "M17 8C8 10 5 16 4 22h2c.7-3 2-5 4-7v6h2v-6c4-1 6-4 5-7zm-2-5C9 4 5 8 7 14c3-3 6-4 8-11z" },
    { label: "Vergleichen", d: "M12 3l4 4-4 4V8H6V6h6V3zm0 18l-4-4 4-4v3h6v2h-6v3z" },
    { label: "Analysieren", d: "M5 21V11h3v10H5zm5 0V3h3v18h-3zm5 0v-7h3v7h-3z" },
    { label: "Entscheiden", d: "M9 16.2l-3.5-3.5L4 14.2 9 19.2 20 8.2l-1.4-1.4L9 16.2z" },
  ];
  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="relative aspect-square">
        {/* center monitor */}
        <div className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-white rounded-2xl border border-[var(--rule)] shadow-lg flex items-center justify-center">
          <div className="text-center px-3">
            <span className="inline-flex w-12 h-12 rounded-full bg-[color-mix(in_oklab,var(--leaf)_15%,white)] items-center justify-center mb-2">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-[var(--leaf-deep)]" fill="currentColor" aria-hidden>
                <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
              </svg>
            </span>
            <div className="text-xs font-display font-semibold text-navy leading-tight">Ihre beste Entscheidung</div>
          </div>
        </div>
        {/* orbit icons */}
        {items.map((it, i) => {
          const angle = (i / items.length) * Math.PI * 2 - Math.PI / 2;
          const r = 44; // %
          const x = 50 + Math.cos(angle) * r;
          const y = 50 + Math.sin(angle) * r;
          return (
            <div
              key={it.label}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <span className="w-11 h-11 rounded-full bg-white border border-[var(--rule)] shadow-sm flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[var(--leaf-deep)]" fill="currentColor" aria-hidden>
                  <path d={it.d} />
                </svg>
              </span>
              <span className="text-[10px] md:text-xs text-navy/70 font-medium">{it.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
